import React, { useState } from "react";
import isEmpty from "lodash/isEmpty";
import styles from "./MultipleBusiness.module.scss";
import dropdownArrowLight from "../../../assets/images/LightTheme/down_arrow_light.svg";
import dropdownArrowDark from "../../../assets/images/DarkTheme/down_arrow_dark.svg";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../redux/hooks";
import ProfileCard from "../ProfileCard/ProfileCard";
import { useProfileParams } from "../../../utils/common";
import {
  useGetLeaderMultipleBusinessesQuery,
  LeaderMultipleBusinessItem,
} from "../../../redux/services/leaderMultipleBusinessesAPI";
import { isValidResponse } from "../../../utils/validateAPIResponse";
import EmptyTable from "../../common/EmptyTable/EmptyTable";

const INITIAL_VISIBLE_CARDS = 6;
const MAX_VISIBLE_CARDS = 20;

const MultipleBusiness = () => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow =
    currentTheme === "light" ? dropdownArrowLight : dropdownArrowDark;
  const [isContentVisible, setIsContentVisible] = useState<boolean>(false);

  const [viewMore, setViewMore] = useState(6);

  const { affiliateCode, aboNumber } = useProfileParams();
  const { data } = useGetLeaderMultipleBusinessesQuery(
    { affiliateCode, aboNumber },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const cards: LeaderMultipleBusinessItem[] = isValidResponse(data?.statusCode)
    ? data?.body || []
    : [];

  const mappedCards = cards
    .filter((item: LeaderMultipleBusinessItem) => {
      const code = +item?.currentAward?.awardCode;
      return !Number.isNaN(code) && code >= 330; // Filter out awards with codes less than 330(Platinum+)
    })
    .map((item: LeaderMultipleBusinessItem) => ({
      aboNumber: String(item?.aboNumber ?? "-"),
      businessName: item?.name || item?.localName || "-",
      countryCode: item?.marketCountryCode || "-",
      award: item?.currentAward?.awardName || "-",
      marketName : item?.marketName || "-",
      isPrimary: !!item?.primary,
    }))
    .sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;

      const nameA = a.marketName.toLowerCase();
      const nameB = b.marketName.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      const numA = parseInt(a.aboNumber, 10);
      const numB = parseInt(b.aboNumber, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.aboNumber.localeCompare(b.aboNumber);
    });

  const handleToggleView = () => {
    if (viewMore === INITIAL_VISIBLE_CARDS) {
      setViewMore(Math.min(mappedCards.length, MAX_VISIBLE_CARDS));
    } else {
      setViewMore(INITIAL_VISIBLE_CARDS);
    }
  };
  const handleDropdownClick = () => {
    setIsContentVisible((prev) => !prev);
  };
  return (
    <div className={styles.multiple_business_wrapper}>
      <div
        className={`${styles.section_title_wraper} ${isContentVisible ? styles.content_visible : ""}`}
      >
        <span className={styles.section_title}>{t("multiple_business")}</span>
        <img
          src={dropdownArrow}
          alt="Dropdown Arrow"
          className={`dropdown_icon ${styles.dropdown_icon} ${isContentVisible ? styles.rotate : ""}`}
          onClick={handleDropdownClick}
          style={{
            cursor: true ? "pointer" : "default",
          }}
        />
      </div>
      {isContentVisible &&
        (isEmpty(mappedCards) ? (
          <EmptyTable message={t("no_additional_business")} />
        ) : (
          <>
            <div className={styles.business_card}>
              {mappedCards.slice(0, viewMore).map((card, index) => (
                <ProfileCard key={index} {...card} />
              ))}
            </div>

            {mappedCards.length > 6 && (
              <div className={styles.viewmore_wrapper}>
                <button className={styles.view_more} onClick={handleToggleView}>
                  {viewMore === INITIAL_VISIBLE_CARDS
                    ? t("view_more")
                    : t("view_less")}
                </button>
              </div>
            )}
          </>
        ))}
    </div>
  );
};

export default MultipleBusiness;
